import React, { useEffect } from 'react';
import ArticleLayout from './ArticleLayout';
import Footer from '../../../../components/Footer/Footer';
import { Link } from 'react-router';
import EconomicMetricsDashboard from './components/EconomicMetricsDashboard/EconomicMetricsDashboard';
import { useGetEconomicMetricsQuery } from 'store/services/economic/economicApi';
import './articles.css';

const SustainableEconomics = () => {
  // Fetch current economic metrics for dynamic content
  const { data: economicData } = useGetEconomicMetricsQuery();
  // SEO: Set document title and meta tags
  useEffect(() => {
    document.title = 'Sustainable Economics - TradeMagic Trading Guide';

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Learn how TradeMagic maintains a healthy trading economy through dynamic point management, variable fees, and sustainable economic controls.'
      );
    }

    // Update Open Graph meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Sustainable Economics - TradeMagic Trading Guide');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute(
        'content',
        'Learn how TradeMagic maintains a healthy trading economy through dynamic point management, variable fees, and sustainable economic controls.'
      );
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://trademagic.gg/articles/sustainable-economics');
    }
  }, []);

  return (
    <ArticleLayout
      title="Sustainable Economics"
      hero="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/articles/hero_sensei_divining_top.png"
    >
      {/* Schema.org markup for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Sustainable Economics',
          description:
            'Learn how TradeMagic maintains a healthy trading economy through dynamic point management, variable fees, and sustainable economic controls.',
          url: 'https://trademagic.gg/articles/sustainable-economics',
          publisher: {
            '@type': 'Organization',
            name: 'TradeMagic',
            url: 'https://trademagic.gg',
          },
        })}
      </script>

      {/* Economic Health Dashboard */}
      <EconomicMetricsDashboard />

      <div className="article-intro">
        <p>
          In any trading economy, the number of points or trade credits circulating in the system is critical to platform health.
          TradeMagic uses dynamic economic controls to maintain optimal trade velocity—keeping trades fast, fair, and
          sustainable as the community grows.
        </p>
      </div>

      <article className="article-content">
        {/* Section: Why Economic Balance Matters */}
        <section className="article-section">
          <h2>Why Economic Balance Matters</h2>
          <p>Think of TradeMagic's economy like a water system: too much pressure and pipes burst, too little and nothing flows.</p>
          <p>
            <strong>The Goldilocks problem:</strong>
          </p>
          <ul className="concept-list">
            <li>
              <strong>Too many points?</strong> Demand drops, trades sit unclaimed, points lose value
            </li>
            <li>
              <strong>Too few points?</strong> Users can't afford cards, trading slows to a crawl
            </li>
            <li>
              <strong>Just right?</strong> High trade velocity, quick claims, stable point values
            </li>
          </ul>
          <p className="goal">
            <strong>Our goal:</strong> Maintain the optimal number of points in circulation to maximize trade velocity—the
            speed at which trades complete and cards change hands.
          </p>
        </section>

        {/* Section: The Challenge */}
        <section className="article-section">
          <h2>The Challenge: Economic Dynamics Are Always Changing</h2>
          <p>Several factors constantly affect the point economy:</p>

          <p>
            <strong>Growth dynamics:</strong>
          </p>
          <ul>
            <li>New users join → more points enter the system (signup bonuses)</li>
            <li>Users leave → they cash out points by claiming trades, transferring balance to active traders</li>
            <li>Platform scales → total points in circulation multiply</li>
          </ul>

          <p>
            <strong>Trading behavior changes:</strong>
          </p>
          <ul>
            <li>Popular cards drive high-value trades (requires more points)</li>
            <li>Meta shifts affect card demand</li>
            <li>Seasonal activity (holiday trading surges, summer slowdowns)</li>
          </ul>

          <p>
            <strong>Supply and demand imbalances:</strong>
          </p>
          <ul>
            <li>Too many senders, not enough buyers → point shortage</li>
            <li>Too many want lists, not enough senders → point oversupply</li>
          </ul>

          <p className="emphasis">
            <strong>The risk:</strong> Without mechanisms to balance these dynamics, the economy can suffer—either from
            inflation or deflation.
          </p>
        </section>

        {/* Section: The Solution */}
        <section className="article-section">
          <h2>The Solution: Dynamic Point Sinks</h2>
          <p>
            TradeMagic uses variable fees as the primary tool to manage point supply. These fees act as "point sinks"—mechanisms
            that remove points from circulation permanently.
          </p>
          <p>
            Think of it like the Federal Reserve adjusting interest rates. As economic conditions shift, we adjust fees to keep
            the economy healthy.
          </p>
        </section>

        {/* Section: Transaction Fee */}
        <section className="article-section">
          <h2>1. Transaction Fee (Variable)</h2>
          <p>Every completed trade includes a small transaction fee, automatically deducted from the sender's earnings.</p>

          <p>
            <strong>How it works:</strong>
          </p>
          <ul className="concept-list">
            <li>
              <strong>Undersupplied economy:</strong> 0.5% fee or even 0% (preserve points, encourage trading)
            </li>
            <li>
              <strong>Healthy economy:</strong> 1-2% fee (mild point destruction)
            </li>
            <li>
              <strong>Oversupplied economy:</strong> 3-5% fee (moderate point destruction to restore demand)
            </li>
          </ul>

          <p>
            <strong>Example:</strong>
          </p>
          <p>You send a $50 card (5,000 points)</p>
          <ul>
            <li>At 0% fee: You earn 5,000 points, 0 points destroyed</li>
            <li>At 1% fee: You earn 4,950 points, 50 points destroyed</li>
            <li>At 5% fee: You earn 4,750 points, 250 points destroyed</li>
          </ul>

          <p>
            <strong>The fee percentage adjusts based on economic indicators like:</strong>
          </p>
          <ul>
            <li>Total points in circulation vs. active users</li>
            <li>Trade initiation rate (are trades being claimed quickly?)</li>
            <li>Point velocity (how fast are points moving through the system?)</li>
          </ul>
        </section>

        {/* Section: Monthly Maintenance Fee */}
        <section className="article-section">
          <h2>2. Monthly Maintenance Fee (Variable)</h2>
          <p>Every user pays a small monthly fee on their account anniversary date.</p>
          <p className="conversion">
            <strong>Current rate:</strong> {economicData?.monthlyPointFee ?? 10} points/month
          </p>

          <p>
            <strong>Why it matters:</strong>
          </p>
          <ul>
            <li>Removes points from inactive accounts (prevents point hoarding)</li>
            <li>Creates consistent, predictable point destruction</li>
            <li>Adjustable over time (can increase or decrease as needed)</li>
          </ul>

          <p>
            <strong>Example:</strong>
          </p>
          <ul>
            <li>User A has 5,000 points, pays {economicData?.monthlyPointFee ?? 10} points monthly ({((((economicData?.monthlyPointFee ?? 10) / 5000) * 100).toFixed(1))}% monthly drain)</li>
            <li>User B has 10,000 points, pays {economicData?.monthlyPointFee ?? 10} points monthly ({((((economicData?.monthlyPointFee ?? 10) / 10000) * 100).toFixed(1))}% monthly drain)</li>
          </ul>

          <p>
            This fee scales with the economy—if inflation becomes an issue, we can increase it. If deflation is the concern, we
            can reduce or pause it.
          </p>
        </section>

        {/* Section: Target Economic Health */}
        <section className="article-section">
          <h2>Target Economic Health: The Math Behind the System</h2>
          <p>
            <strong>Current targets:</strong>
          </p>
          <p>With approximately 100 active traders, we aim for:</p>
          <ul>
            <li>2,000-3,000 points per active trader</li>
            <li>200,000-250,000 total points in circulation</li>
            <li>$3-5 average card value (300-500 points per card)</li>
          </ul>

          <p className="summary">
            <strong>What this means:</strong> Users hold 2,000-3,000 points at any moment, but cycle through 15,000-50,000
            points over weeks or months as they trade. This creates a healthy flow without hoarding.
          </p>

          <p>
            <strong>Trade velocity indicators:</strong>
          </p>
          <ul className="checklist">
            <li>Average time to claim a trade: Under 72 hours (healthy)</li>
            <li>Percentage of want lists with sufficient buyers: &gt;80% (healthy)</li>
            <li>Point turnover rate: Each point changes hands 6-10 times per year (healthy)</li>
          </ul>

          <p>
            If these metrics drop, fees adjust upward to reduce supply. If metrics surge too high (indicating shortage), fees
            drop or pause.
          </p>
        </section>

        {/* Section: Future Point Sinks */}
        <section className="article-section">
          <h2>Future Point Sinks (Coming Soon)</h2>
          <p>
            As TradeMagic grows, we'll introduce additional tools to manage the economy while enhancing your experience:
          </p>

          <p>
            <strong>Community Recognition:</strong>
          </p>
          <ul>
            <li>
              <strong>Trader Commendations</strong> – Award badges to traders who provide exceptional service (fast shipping,
              mint condition cards, great communication)
            </li>
            
          </ul>

          <p>
            <strong>Smarter Trading:</strong>
          </p>
          <ul>

            <li>
              <strong>Bundle Bonuses</strong> – Offer point multipliers to incentivize traders to send you multiple cards at
              once
            </li>
          </ul>

          <p>
            <strong>Platform Participation:</strong>
          </p>
          <ul>
            <li>
              <strong>Feature Voting</strong> – Vote with points on which features get prioritized in development
            </li>
            <li>
              <strong>Sponsor New Traders</strong> – Pay points to give referred friends bonus starting points
            </li>
          </ul>

          <p className="callout">
            Each feature adds flexibility to adjust point supply without raising fees, while giving you meaningful ways to
            enhance your trading experience.
          </p>
        </section>

        {/* Section: Transparency & Trust */}
        <section className="article-section">
          <h2>Transparency & Trust</h2>
          <p>
            <strong>You deserve to know how the economy works.</strong>
          </p>

          <p>
            <strong>On this page, we publish:</strong>
          </p>
          <ul className="checklist">
            <li>Current transaction fee percentage</li>
            <li>Current monthly fee</li>
            <li>Key economic indicators</li>
          </ul>

          <p>
            <strong>Our commitment:</strong>
          </p>
          <ul className="checklist">
            <li>Point-fees only change when economic data justifies it</li>
            <li>Planned point-fee changes are announced via system message</li>
            <li>We'll never surprise you with hidden costs</li>
          </ul>
        </section>

        {/* Section: The Bottom Line */}
        <section className="article-section cta-section">
          <h2>The Bottom Line</h2>
          <p>TradeMagic's economy is designed to be sustainable and self-regulating.</p>

          <p>
            <strong>By using variable fees as dynamic point sinks, we can:</strong>
          </p>
          <ul className="icon-list">
            <li>📈 Maintain healthy trade velocity as the platform grows</li>
            <li>⚖️ Prevent inflation (too many points) or deflation (too few points)</li>
            <li>💎 Ensure points hold their value over time</li>
            <li>⚡ Keep trading fast, fair, and friction-free</li>
          </ul>

          <p className="emphasis">TradeMagic has the tools to adapt and thrive for the long term.</p>

          <p className="welcome">
            <strong>Your role:</strong> Trade actively, check in cards promptly, and trust that the economic systems are
            working behind the scenes to keep the platform healthy.
          </p>
        </section>

        {/* Section: Questions */}
        <section className="article-section footer-section">
          <p>
            Want to dive deeper into the economics? Email{' '}
            <a href="mailto:support@trademagic.gg" className="article-link">
              support@trademagic.gg
            </a>{' '}
            or share your thoughts from the Feedback link in your sidebar.
          </p>
          <p>
            <strong>Related Articles:</strong>
          </p>
          <ul>
            <li>
              <Link to="/articles/how-it-works" className="article-link">
                How TradeMagic Works
              </Link>
            </li>
            <li>
              <Link to="/articles/shipping" className="article-link">
                Our Shipping Guide
              </Link>
            </li>
            <li>
              <Link to="/articles/condition-guide" className="article-link">
                Our Condition Guide
              </Link>
            </li>
          </ul>
          <p className="welcome">
            <strong>Happy Trading! 🎴</strong>
          </p>
        </section>
      </article>

      <Footer />
    </ArticleLayout>
  );
};

export default SustainableEconomics;
